'use client';

import Image from 'next/image';
import { useRef, Fragment } from 'react';
import { easeIn, easeInOut, easeOut, motion, useScroll, useTransform } from 'framer-motion';
import { PortableTextBlock } from 'sanity';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { useIsMobile } from '@/hooks/useIsMobile';
import ChipText from '@/components/global/ChipText';
import {GROQImage} from "@/sanity/lib/types";
import qualitySectionBg from '@/public/home/qualitySectionBg.png';
import qualitySectionBgMobile from '@/public/home/qualitySectionBgMobile.png';

interface IQualityProps {
  title: PortableTextBlock[];
  items: {
    icon: GROQImage;
    title: string;
    description: string;
  }[];
}

export default function Quality({
  title,
  items,
}: IQualityProps) {
  const isMobile = useIsMobile();

  return (
    <section className="flex flex-col w-full items-end justify-center pt-[60.66rem] lg:pt-[17.25rem] pb-[15rem] lg:pb-[22.12rem] lg:pr-[16.12rem] relative bg-offwhite">
      <Image
        src={isMobile ? qualitySectionBgMobile : qualitySectionBg}
        alt="Image Background"
        layout="fill"
        className="absolute top-0 left-0 !w-full !h-auto lg:object-cover lg:object-center z-0"
        quality={100}
      />

      <div className="flex flex-col gap-[3rem] lg:gap-[8rem] w-full max-w-[84.875rem] relative z-10 px-[2rem] lg:px-0">
        <PortableText value={title} components={richTextComponent} />

        <div className="flex flex-col gap-[4rem]">
          {items?.length > 0 && items?.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: -50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: easeOut, delay: index * 0.15 }}
              viewport={{ once: true }}
              className="flex self-stretch gap-[3rem] lg:gap-[4rem] py-[1rem] w-fit"
            >
              <div className="flex flex-col w-[7rem] lg:w-[8rem] h-[7rem] lg:h-[8rem] items-center justify-center bg-green flex-shrink-0">
                <Image
                  className="!w-[3rem] lg:!w-[4rem] aspect-square"
                  src={item.icon.src}
                  width={item.icon.width}
                  height={item.icon.height}
                  quality={100}
                  alt=""
                />
              </div>
              <div className="flex flex-col gap-[1rem]">
                <h3 className="text-[3rem] lg:text-[4rem] font-[400] leading-[120%] font-inter-tight mb-0">{item.title}</h3>
                <p className="text-[2rem] font-[400] text-[#072922]/50 leading-[140%] font-inter-tight max-w-[60.625rem] mb-0">{item.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
